import {
  API_TYPES,
  type GetWorkerRequirementsResponse,
  WorkerRequirementMappingLevel,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { type UseGetQueryOptions } from "@src/appV2/api";
import { useFilterBackgroundCheckRequirements } from "@src/appV2/redesign/BackgroundChecks/useFilterBackgroundCheckRequirements";
import { type AxiosError } from "axios";

import { useDocumentExclusionFilter } from "../../hooks/useDocumentExclusionFilter";
import { mergeWorkerRequirementWithIncludedResources } from "../mergeWorkerRequirementWithIncludedResources";
import { type WorkerRequirement } from "../types";
import { useGetWorkerRequirements } from "../useGetWorkerRequirements";

interface UseGetMandatoryWorkerRequirementsProps {
  qualifications: string[];
}

export function useGetMandatoryWorkerRequirements(
  props: UseGetMandatoryWorkerRequirementsProps,
  options: UseGetQueryOptions<GetWorkerRequirementsResponse, AxiosError, WorkerRequirement[]> = {}
) {
  const { qualifications } = props;

  const filterDocuments = useDocumentExclusionFilter();

  const { filterOutBackgroundCheckRequirements } = useFilterBackgroundCheckRequirements();

  return useGetWorkerRequirements(
    {
      filter: {
        qualifications,
        requiredForBooking: true,
        levels: [
          WorkerRequirementMappingLevel.GLOBAL,
          WorkerRequirementMappingLevel.STATE,
          WorkerRequirementMappingLevel.COUNTY,
          WorkerRequirementMappingLevel.MSA,
        ],
        visibleToWorker: true,
      },
      include: [API_TYPES.requirements, API_TYPES.requirementMappings],
    },
    {
      select: (data) => {
        const mergedData = mergeWorkerRequirementWithIncludedResources(data);
        const filteredData = filterDocuments(mergedData);

        // If background check is MISSING or EXPIRED, we want to hide it (filter it out) as we don't want the worker to know
        // about it since they cannot take an action on it (as it runs automatically on booking).
        return filterOutBackgroundCheckRequirements({
          workerRequirements: filteredData,
          filter: {
            status: [WorkerRequirementStatus.MISSING, WorkerRequirementStatus.EXPIRED],
          },
        });
      },
      ...options,
    }
  );
}
